import findIndex from 'lodash/findIndex';
import map from 'lodash/map';
import zipWith from 'lodash/zipWith';

import { getUuidFrom, getIdFrom } from 'zoomforth_codesbane_viewer/lib/id_utils.js';

import { getSerializableContentDataForCopy }
  from 'zoomforth_codesbane_viewer/lib/page_content_utils.js';

import {
  getEidByContentUuid,
  createNewLoadingContent,
  updateContentDataForEid,
  removeContentDataForEid,
  removeContentItemForEid
} from 'zoomforth_codesbane_editor/lib/section_overrides/ordered_content_section_overrides.js';

import {
  showErrorNotification
} from './misc.js';

import {
  getEditorOnlySectionOverrides
} from 'zoomforth_codesbane_editor/reducers/index.js';

import {
  getContentItemByUuid,
  getSectionById,
  getSectionForContent,
  getSectionOrderedContentItems,
  getOrderedContentUuidsForSection,
  getAssetForContent
} from 'zoomforth_codesbane_viewer/reducers/index.js';

import {
  uploadFileAndFetchAsset
} from 'zoomforth_codesbane_editor/lib/assets.js';

import {
  fetchAndUpdateAssetData
} from './asset.js';

import {
  createContentItem,
  deleteContentItems,
  setContentItemSimpleData,
  createContentItemFromAsset
} from './content.js';

import {
  setSectionAndOverrides,
  removeSection
} from './sections.js';

import {
  setEditingContent,
  endEditingContent
} from './misc.js';

import {
  showModal
} from './editor_modals.js';

import ConfirmDeleteSlideshowItemModal
  from 'zoomforth_codesbane_editor/exterior_editor/editor_modals/confirm_delete_slideshow_item_container.jsx';

import ConfirmDeleteAccordionItemModal
  from 'zoomforth_codesbane_editor/exterior_editor/editor_modals/confirm_delete_accordion_item_container.jsx';

import ConfirmDeleteVideoplaylistItemModal
  from 'zoomforth_codesbane_editor/exterior_editor/editor_modals/confirm_delete_videoplaylist_item_container.jsx';

export const updateSectionOverridesForEid = (sectionOrId, eid, newData) =>
  (dispatch, getState) => {
    const section = getSectionById(getState(), getIdFrom(sectionOrId));
    const sectionOverrides = getEditorOnlySectionOverrides(getState(), section.id);

    const [newSection, newSectionOverrides] = updateContentDataForEid(section, sectionOverrides, eid, newData);

    dispatch(setSectionAndOverrides(section.id, newSection, newSectionOverrides));
  };


export const removeSectionOverridesForEid = (sectionOrId, eid) =>
  (dispatch, getState) => {
    const section = getSectionById(getState(), getIdFrom(sectionOrId));
    const sectionOverrides = getEditorOnlySectionOverrides(getState(), section.id);

    const [ newSection, newSectionOverrides ] = removeContentDataForEid(section, sectionOverrides, eid);

    dispatch(setSectionAndOverrides(section.id, newSection, newSectionOverrides));
  };

export const addNewContentItemToOrderedContentSection = (sectionOrId, contentData, positionData={}, options={}) => 
  (dispatch, getState) => {
    const section = getSectionById(getState(), getIdFrom(sectionOrId));
    const sectionOverrides = getEditorOnlySectionOverrides(getState(), section.id);

    // Create a new loading instance
    const [newEid, newSection, newSectionOverrides] = createNewLoadingContent(section, sectionOverrides);

    // Save the new override data with the loading content
    dispatch(setSectionAndOverrides(section.id, newSection, newSectionOverrides));
    
    // Don't auto-edit when inside a tab to prevent automatic entry into accordion items
    const shouldEditOnCreate = options.shouldEditOnCreate !== undefined ? options.shouldEditOnCreate : !section._isInsideTab;
    
    dispatch(createContentItem(section, contentData, positionData, { shouldEditOnCreate, shouldScrollToOnCreate: true }));
  };

export const addNewAssetToOrderedContentSection = (sectionOrId, asset, positionData={}, { isForReplace=false } = {}) =>
  (dispatch, getState) => {
    const section = getSectionById(getState(), getIdFrom(sectionOrId));
    const sectionOverrides = getEditorOnlySectionOverrides(getState(), section.id);

    // Create a new loading instance
    const [newEid, newSection, newSectionOverrides] = createNewLoadingContent(section, sectionOverrides, { positionData });

    // Save the new override data with the loading content
    dispatch(setSectionAndOverrides(section.id, newSection, newSectionOverrides));

    // Create the actual content item -- the saving function deals with
    // updating the override data and replacing it with the final asset
    dispatch(createContentItemFromAsset(newSection, asset, { eid: newEid, ...positionData }, {}, { isForReplace, shouldEditOnCreate: false }));
  };


export const replaceContentItemInOrderedContentSection = (contentItem, asset) =>
  (dispatch, getState) => {
    const section = getSectionForContent(getState(), contentItem);
    const sectionOverrides = getEditorOnlySectionOverrides(getState(), section.id);
    const eid = getEidByContentUuid(section, sectionOverrides, contentItem.uuid);
    const orderedContentItems = getSectionOrderedContentItems(getState(), section);
    const indexForNewContent = findIndex(
      orderedContentItems, item =>
        contentItem.uuid === item.uuid
    );

    const positionData = { atIndex: indexForNewContent };

    // @NOTE we used to do these actions for replacement but they just don't
    // work. basically we delete the content item so it messes up indexing and
    // loading state. instead of that we choose to use the `isForReplace` logic
    // we use in the grid. the downside is intermediate loading state isn't
    // going to show up but this should be generally fine. it's certainly
    // better than the bug where the the next index is completely deleted.
    //
    // dispatch(deleteContentItemFromOrderedContentSection(contentItem));
    // dispatch(addNewAssetToOrderedContentSection(section, asset, positionData, { isForReplace: true }));

    // Create the actual content item -- the saving function deals with
    // updating the override data and replacing it with the final asset
    dispatch(createContentItemFromAsset(section, asset, { eid, ...positionData }, {}, { isForReplace: true }));
  };


export const deleteContentItemFromOrderedContentSection = (contentItem)=>
  (dispatch, getState) => {
    const section = getSectionForContent(getState(), contentItem);
    const sectionOverrides = getEditorOnlySectionOverrides(getState(), section.id);
    const eid = getEidByContentUuid(section, sectionOverrides, contentItem.uuid);

    dispatch(updateSectionOverridesForEid(section, eid, {
      isLoading: true,
      isInIntermediateState: true
    }));

    // After we update the section overrides we need to fetch them again to
    // have a true representation of the section state
    const updatedSectinOverrides = getEditorOnlySectionOverrides(getState(), section.id);

    const [newSection, newSectionOverrides] = removeContentItemForEid(section, updatedSectinOverrides, eid);

    dispatch(endEditingContent([contentItem]));
    dispatch(setSectionAndOverrides(newSection.id, newSection, newSectionOverrides));

    dispatch(deleteContentItems([contentItem]));
  };


export const reorderContentInOrderedContentSection = (section, contentItems)=>
  (dispatch, getState) => {
    const sectionOverrides = getEditorOnlySectionOverrides(getState(), section.id);
    const newSection = {
      ...section,
      orderedContentUuids: map(contentItems, i => i.uuid ? i.uuid : i)
    };

    dispatch(setSectionAndOverrides(newSection.id, newSection, sectionOverrides));
  };
  


export const deleteOrderedContentSection = (sectionOrId)=>
  (dispatch, getState)=> {
    const section = getSectionById(getState(), getIdFrom(sectionOrId));
    const contentUuids = getOrderedContentUuidsForSection(getState(), section);
    // Remove section first, otherwise we will attempt to render missing content
    dispatch(removeSection(section));
    dispatch(deleteContentItems(contentUuids));
  };



export const setDisplayImageFromVideoFrame = (contentItem, videoFrameSrc)=>
  (dispatch)=> {

    dispatch(setContentItemSimpleData(contentItem.uuid, {
      tile_display_type: 'video_frame', // OLD HACK: Why the fuck does `*tile*_display_foobar` effect a not-tile thing?
      tile_display_image: videoFrameSrc,
      thumbnail: videoFrameSrc
    }));

  };


export const uploadAssetToOrderedContentSection = (sectionOrId, file) =>
  (dispatch, getState)=> {
    const sectionId = getIdFrom(sectionOrId);
    const section = getSectionById(getState(), sectionId);
    const sectionOverrides = getEditorOnlySectionOverrides(getState(), sectionId);

    const [newEid, newSection, newSectionOverrides] = createNewLoadingContent(section, sectionOverrides);
    dispatch(setSectionAndOverrides(newSection.id, newSection, newSectionOverrides));

    uploadFileAndFetchAsset(file, { 
      uploadContext: 'page',
      onProgressChange:(progressData)=> {
        dispatch(updateSectionOverridesForEid(section, newEid, {
          ...progressData,
          isLoading: true,
          isInIntermediateState: true
        }));
      }, 
      onAssetReceive:(asset)=> {
        if (asset.isEncoding) {
          dispatch(updateSectionOverridesForEid(section, newEid, {
            isEncoding: asset.isEncoding,
            isInIntermediateState: true
          }));
        } else {
          dispatch(removeSectionOverridesForEid(newSection, newEid));
        }
        dispatch(createContentItemFromAsset(newSection, asset, { eid: newEid }, {}, { shouldEditOnCreate: false }));
      }, 
      onError:(errorData={})=> {
        const errorMessage = errorData.error  || 'Something went wrong while uploading your file. Please try again later.';
        dispatch(updateSectionOverridesForEid(newSection, newEid, { error:errorMessage }));

        dispatch(showErrorNotification('Upload failed', errorMessage));

        setTimeout(()=> {
          dispatch(removeSectionOverridesForEid(newSection, newEid));
        }, 5000);
      } 
    });
  };


export const updateOrderedContentSectionWithNewContent = (section, contentItem, positionData={}, { shouldEditOnCreate } = {}) =>
  (dispatch, getState) => {
    const { eid, atIndex=-1 } = positionData;

    let orderedContentUuids = getOrderedContentUuidsForSection(getState(), section);

    let updatedOrderedContentUuids = new Array(
      atIndex > orderedContentUuids.length ?
        atIndex
      : orderedContentUuids.length
    );

    // determine where the new contentItem should be placed in the ordered set

    if (atIndex >= 0) {
      updatedOrderedContentUuids.splice(atIndex, 0, contentItem.uuid);
      updatedOrderedContentUuids = zipWith(
        updatedOrderedContentUuids,
        orderedContentUuids,
        (a, b) => (a || b)
      );

    } else {
      updatedOrderedContentUuids = [ ...orderedContentUuids, contentItem.uuid ];
    }

    const asset = getAssetForContent(getState(), contentItem);

    var newSection = {
      ...section,
      orderedContentUuids: updatedOrderedContentUuids
    };
    var newSectionOverrides = {};

    // If an EID was set, we want to remove the data for it since it should no
    // longer be in a loading state. however, if the asset is still encoding
    // then we want to persist the encoding data.
    if (eid && asset && !asset.isEncoding) {
      const sectionOverrides = getEditorOnlySectionOverrides(getState(), newSection.id);
      [ newSection, newSectionOverrides ] = removeContentDataForEid(newSection, sectionOverrides, eid);
    }

    dispatch(setSectionAndOverrides(newSection.id, newSection, newSectionOverrides));

    if (shouldEditOnCreate) {
      dispatch(setEditingContent([contentItem]));
    }
  };


export const updateOrderedContentSectionAfterEncoding = (section, contentUuid) =>
  (dispatch, getState) => {
    const sectionOverrides = getEditorOnlySectionOverrides(getState(), section.id);
    const eid = getEidByContentUuid(section, sectionOverrides, contentUuid);

    dispatch(removeSectionOverridesForEid(section, eid));
  };

export const duplicateAccordionItem = (contentItemOrUuid) =>
  (dispatch, getState) => {
    const contentItemUuid = getUuidFrom(contentItemOrUuid);
    const contentItem = getContentItemByUuid(getState(), contentItemUuid);
    const section = getSectionForContent(getState(), contentItem);

    dispatch(createContentItemFromContentForOrderedContentSection(
      section,
      contentItem
    ));
  };

export const confirmDeleteAccordionItem = (contentItemOrUuid) => 
  (dispatch, getState) => {
    const contentItemUuid = getUuidFrom(contentItemOrUuid);
    const contentItem = getContentItemByUuid(getState(), contentItemUuid);
    const section = getSectionForContent(getState(), contentItem);

    if(contentItem.accordion_data && contentItem.accordion_data.label){
      dispatch(showModal(ConfirmDeleteAccordionItemModal, { section, contentItem }));
    }else{
      dispatch(deleteContentItemFromOrderedContentSection(contentItem));
    }

  };

export const confirmDeleteSlideshowItem = (contentItemOrUuid)=>
  (dispatch, getState)=> {
    const contentItemUuid = getUuidFrom(contentItemOrUuid);
    const contentItem = getContentItemByUuid(getState(), contentItemUuid);
    const section = getSectionForContent(getState(), contentItem);

    dispatch(showModal(ConfirmDeleteSlideshowItemModal, { section, contentItem }));
  };


export const confirmDeleteVideoplaylistItem = (contentItemOrUuid)=>
  (dispatch, getState)=> {
    const contentItemUuid = getUuidFrom(contentItemOrUuid);
    const contentItem = getContentItemByUuid(getState(), contentItemUuid);
    const section = getSectionForContent(getState(), contentItem);

    dispatch(showModal(ConfirmDeleteVideoplaylistItemModal, { section, contentItem }));
  };

export const createContentItemFromContentForOrderedContentSection = (section, contentItem, positionData={}, postCreateData={})=>
  (dispatch, getState)=> {
    const sectionOverrides = getEditorOnlySectionOverrides(getState(), section.id);
    const [newEid, newSection, newSectionOverrides] = createNewLoadingContent(section, sectionOverrides);

    if (contentItem.asset_type && contentItem.asset_id) {
      dispatch(fetchAndUpdateAssetData(contentItem.asset_type, contentItem.asset_id));
    }

    dispatch(setSectionAndOverrides(section.id, newSection, newSectionOverrides));
    dispatch(createContentItem(newSection, getSerializableContentDataForCopy(contentItem), { eid: newEid, ...positionData }, postCreateData));
  };
